import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const LAST_UPDATED = 'July 8, 2026'

function Em({ children }) {
  return <span className="font-medium text-foreground">{children}</span>
}

function Block({ title, children }) {
  return (
    <Card className="gap-2 bg-background">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  )
}

export default function PrivacyTerms() {
  return (
    <div id="privacy" className="space-y-6">
      <p className="text-xs text-muted-foreground">Last updated: {LAST_UPDATED}</p>

      <Block title="Privacy at a glance">
        <p>
          By design, Retira.app is <Em>local-first</Em>. The employee census you enter stays in your browser,
          calculations run on your device, and census data is never transmitted to us.
        </p>
        <p>
          If you sign in, we store your account information and the plan-design settings associated with your account
          in our cloud, so your saved plan settings are available across sessions and devices. Your <Em>employee
          census is never uploaded</Em> – it stays in your browser.
        </p>
        <p>
          The app includes optional AI features. Every one of them is <Em>opt-in</Em>: nothing is sent to an AI
          provider until you explicitly agree, each feature asks for its own consent the first time you use it, and
          declining never breaks anything – the feature simply stays off or falls back to a local, on-device
          alternative. Even when you do opt in, <Em>your census is never uploaded</Em>.
        </p>
      </Block>

      <Block title="Data that stays in your browser">
        <p>
          The employee census, working plan rules, compare-mode plans, Estimate Mode assumptions and the census
          generated from them, and undo history are kept in your browser’s local storage so your work persists
          between sessions. This includes compensation, balances, dates, and
          other personal details in the census.
        </p>
        <p>
          We do not receive census data. You can remove browser-stored data by clearing the census in the app or
          clearing browser storage for this site. On a shared device, consider a private window and clear site
          data when you finish.
        </p>
      </Block>

      <Block title="Accounts &amp; sign-in">
        <p>
          Signing in to the studio is passwordless: we email you a <Em>one-time six-digit code</Em> that you enter
          to sign in. To provide this we store your email address, basic account and access-grant records, and a
          log of sign-in events (such as time and success) used for security and access control. Authentication is
          handled by our infrastructure provider, Supabase.
        </p>
        <p>
          We use your email to operate your account and, where relevant, to contact you about access. We do not
          sell your personal information.
        </p>
      </Block>

      <Block title="Saved plan settings">
        <p>
          Your plan-design settings are stored in our cloud so they stay available across sessions and devices. The
          Client Book lets you keep and label multiple named versions. In all cases, <Em>census data is never
          uploaded</Em>.
        </p>
        <p>
          Selecting a Client Book entry applies its plan settings to the census already open on that device. You can
          delete saved entries at any time.
        </p>
      </Block>

      <Block title="Optional AI features (all opt-in)">
        <p>
          Some features can use a US-based AI provider (no retention, no training on your data) to save you time.
          Each one is <Em>opt-in</Em>: the first time you use it, the app tells you exactly what would leave your
          browser and asks for your consent before sending anything. If you decline, the feature stays off or the
          app falls back to a local, on-device alternative. In every case, <Em>your census is never uploaded</Em> –
          each feature sends only the minimum described below.
        </p>
        <p>
          <Em>Julio, the AI assistant</Em>, does nothing until you turn it on, and you can turn it off at any time.
          When you ask a question, that question, your plan design settings, and <Em>de-identified</Em>{' '}
          employee data – with names and IDs removed in your browser – are sent to generate an answer. Employee{' '}
          <Em>names and IDs never leave your browser</Em>: they are stripped before anything is sent and re-attached
          on your device. Answers are informational only and are not legal, tax, or investment advice.
        </p>
        <p>
          <Em>Census import – “Match with AI”.</Em> To help match your spreadsheet’s columns, we send only the
          column <Em>headers</Em> and masked format samples in which every digit is replaced with a 9 and every
          letter with an X. <Em>No actual cell values</Em> – no names, compensation, dates, or other census
          contents – are sent.
        </p>
        <p>
          <Em>Plan-document import.</Em> When you import plan settings from a plan document, the text is extracted
          from your file <Em>on your device</Em>, and only that document text is sent so plan rules can be read out
          of it. The suggested settings are checked against the app’s allowed values on your device before anything
          is applied, and nothing changes until you review and confirm.
        </p>
        <p>
          <Em>Estimate Mode prefill.</Em> If you ask the AI to fill in the estimate form, only the rough plan
          description <Em>you typed</Em> is sent. The estimated census itself is generated entirely in your
          browser and is never uploaded.
        </p>
      </Block>

      <Block title="Exports &amp; files">
        <p>
          PDF reports, results and census CSVs, and plan-settings JSON are generated on your device and saved by
          you. Once downloaded, these files are under your control and can contain compensation and personal
          data, so handle and share them accordingly.
        </p>
      </Block>

      <Block title="Cookies &amp; browser storage">
        <p>
          We use local/session storage and necessary cookies to keep you signed in and to remember your
          preferences and work in progress.
        </p>
      </Block>

      <Block title="Retention &amp; your choices">
        <p>
          Browser-stored data persists until you clear it. Account and sign-in records are retained while your
          account is active and as needed for security and legal obligations. Saved Client Book entries remain until
          you delete them or your account.
        </p>
        <p>
          To access, correct, or delete your data, use the in-app controls or contact us. Subject to applicable
          law, you may have rights to access, correct, delete, or port your personal information.
        </p>
      </Block>

      <Block title="Contact">
        <p>
          Reach us through the <Em>Contact</Em> link in the app for any privacy request or question about this
          policy.
        </p>
      </Block>

      <Block title="Terms of Use">
        <p>
          By accessing or using Retira.app (the “Service”), provided by <Em>Retira LLC</Em>, you agree to these
          Terms. If you do not agree, do not use the Service.
        </p>
        <p>
          <Em>Informational only, not advice.</Em> The Service models 401(k) contributions for illustration and
          planning. It does not provide legal, tax, accounting, actuarial, or investment advice, is not a
          compliance or nondiscrimination test, and does not create a fiduciary or advisory relationship.
        </p>
        <p>
          <Em>Your responsibilities.</Em> You are responsible for the accuracy of the data you enter, for the
          confidentiality of any census or personal data you handle, and for verifying results and compliance
          with applicable regulations and plan documents before relying on them. Confirm plan-design decisions
          with your recordkeeper, TPA, or ERISA counsel.
        </p>
        <p>
          <Em>Accounts &amp; acceptable use.</Em> You are responsible for activity under your account, for keeping
          access to your email secure, and for having authority to use the data you enter. You agree not to attempt
          to access other users’ data, disrupt the Service, or reverse-engineer it.
        </p>
        <p>
          <Em>Estimates &amp; no warranty.</Em> Outputs are estimates based on the inputs you supply and the IRS
          limits configured for the selected plan year. The Service is provided “as is” and “as available,”
          without warranties of any kind, including accuracy, completeness, availability, or fitness for a
          particular purpose.
        </p>
        <p>
          <Em>Limitation of liability.</Em> To the maximum extent permitted by law, Retira LLC is not liable for
          any indirect, incidental, consequential, or special damages, or for decisions or filings made in
          reliance on the Service. Use of the Service is at your own risk.
        </p>
        <p>
          <Em>Changes.</Em> We may update the Service and these Terms. Material changes will be reflected by the
          “last updated” date above; continued use after an update constitutes acceptance.
        </p>
      </Block>
    </div>
  )
}
